/**
 * El canal social es UNA sola instancia con id conocido.
 *
 * El cliente entra por `joinById`, no por `joinOrCreate`: pedir por nombre podría
 * levantar un segundo mundo si el primero estuviera lleno, y entonces media cuenta
 * recibiría los avisos y la otra media no.
 */
export const SOCIAL_ROOM_NAME = "social";
export const SOCIAL_ROOM_ID = "social";
/**
 * Capacidad del canal social, con margen sobre el objetivo de 100 concurrentes.
 *
 * Es por CONEXIÓN, no por cuenta: una cuenta con tres pestañas ocupa tres asientos.
 */
export const SOCIAL_MAX_CLIENTS = 400;
export const MAX_DM_LENGTH = 300;
/** Cuántos mensajes devuelve una página de conversación por defecto, y el techo. */
export const DM_PAGE_SIZE = 30;
export const MAX_DM_PAGE_SIZE = 100;
export const SEARCH_MIN_LENGTH = 2;
export const SEARCH_LIMIT = 20;
/**
 * Cuántas veces se pide una foto de la base antes de rendirse por esta vez.
 *
 * El cliente descarta el snapshot cuyo viaje coincidió con un aviso —pudo armarse antes
 * de ese aviso, y escribirlo encima lo borraría— y vuelve a pedirlo. Si ninguna ronda
 * sale limpia **no se aplica ninguna** y se reintenta más tarde: quedarse un momento sin
 * la foto es inofensivo, porque los avisos ya se aplicaron en vivo.
 */
export const SNAPSHOT_MAX_ROUNDS = 4;
/**
 * Topes de la libreta. No son antiabuso: son lo que mantiene barato el snapshot,
 * que se manda entero en cada login.
 */
export const MAX_FRIENDS = 100;
export const MAX_PENDING_OUT = 25;
export const MAX_PENDING_IN = 25;
/**
 * Solicitudes de ingreso a club abiertas a la vez, por jugador.
 *
 * Más bajo que el de amistad porque hay cinco clubes y el tope de pertenencia son
 * tres: pedir entrada a los cinco a ver cuál cae primero no es un uso, es una forma
 * de saltarse `MAX_CLUBS_PER_PLAYER` dejando que otros decidan por ti.
 */
export const MAX_CLUB_REQUESTS = 3;
/** Largo de la descripción que el presidente puede escribir. */
export const MAX_CLUB_BLURB = 160;
/** Largo de un anuncio. Más que un DM: es un comunicado, no una conversación. */
export const MAX_ANNOUNCEMENT_LENGTH = 500;
/**
 * Anuncios por club y por hora.
 *
 * Va por CLUB y no por cuenta a propósito: el tope protege a los miembros de que les
 * llegue ruido, y quién lo mandó da igual — si mañana el club cambia de presidente en
 * medio de una tanda, el buzón de los miembros no debería empezar de cero.
 */
export const MAX_ANNOUNCEMENTS_PER_HOUR = 4;
/** Cuántos anuncios se guardan y se devuelven al abrir un club. */
export const ANNOUNCEMENT_PAGE = 20;
/**
 * Rate limits POR CUENTA, no por conexión ni por IP.
 *
 * Por conexión no sirve: abrir una segunda pestaña multiplicaría el tope. Por IP
 * tampoco, porque en la wifi del campus cientos comparten la de salida.
 */
export const DM_BURST = 5;
export const DM_WINDOW_MS = 5000;
export const SOCIAL_MUTATION_BURST = 20;
export const SOCIAL_MUTATION_WINDOW_MS = 60_000;
/** Lecturas HTTP combinadas: snapshot, conversaciones, leídos y clubes. */
export const SOCIAL_READ_BURST = 120;
export const SOCIAL_READ_WINDOW_MS = 60_000;
export const SEARCH_BURST = 30;
export const SEARCH_WINDOW_MS = 60_000;
/**
 * Cada cuánto el canal social revalida los tokens de quien está conectado.
 *
 * Una sesión social dura todo el login, así que un token revocado a mitad —cierre de
 * sesión en otro dispositivo, expiración— seguiría recibiendo avisos privados hasta
 * que el socket se cayera. Esta barrida los echa.
 */
export const SOCIAL_TOKEN_SWEEP_MS = 5 * 60_000;
/** Cuánto se le regala a la caja de un club al aprobarlo. */
export const CLUB_FOUNDING_GRANT = 200;
/* ───────────────────────────── eventos ───────────────────────────── */
export const MAX_EVENT_TITLE = 60;
/** Lo más lejos que se puede convocar algo: dos semanas. */
export const MAX_EVENT_AHEAD_MS = 14 * 24 * 60 * 60 * 1000;
/** Cuánto puede durar una convocatoria, como mucho. */
export const MAX_EVENT_MINUTES = 180;
export const MIN_EVENT_MINUTES = 10;
/** Eventos por delante que un club puede tener a la vez. */
export const MAX_OPEN_EVENTS = 5;
/* ───────────────── competencia: puntos, temporada y nivel ───────────────── */
/**
 * De dónde sale un punto. Es el equivalente de `LedgerReason` para la competencia, y
 * cumple el mismo papel: el `ref` de cada movimiento se arma con esto, así que agregar
 * una razón obliga a decidir cómo se evita contarla dos veces.
 */
export const CLUB_POINT_REASONS = [
    "asistencia",
    "evento",
    "juego",
    "volley",
    "miembro",
    "aporte",
    "decorado",
];
export function isClubPointReason(value) {
    return typeof value === "string" && CLUB_POINT_REASONS.includes(value);
}
/** Cuánto vale cada cosa. En un solo sitio para poder discutir el equilibrio sin buscar. */
export const CLUB_POINT_VALUES = {
    /** Aparecer a un evento de tu club. Lo que más vale: es lo más difícil de juntar. */
    asistencia: 10,
    /** Convocar uno y que venga alguien. Premia organizar, no anunciar. */
    evento: 15,
    /** Terminar una partida de un minijuego. Poco, porque se repite mucho. */
    juego: 1,
    /** Ganar un partido de volley. */
    volley: 5,
    /** Por miembro activo, contado una vez por temporada. */
    miembro: 3,
    /** Por cada 100 monedas aportadas a la caja. */
    aporte: 2,
    /** Por mueble puesto en la sede, contado una vez por mueble y temporada. */
    decorado: 1,
};
/**
 * El nivel de un club sale de sus puntos de SIEMPRE, no de los de la temporada.
 *
 * Dos marcadores distintos a propósito: la temporada es una carrera que se reinicia —si
 * no, el club más viejo gana para siempre— y el nivel es lo que el club ha construido,
 * que no tendría sentido perder cada vez que empieza una.
 *
 * Lo que desbloquea NO es gente: un nivel que limitara cuántos miembros caben convertiría
 * crecer en un castigo. Desbloquea sitio y cara — cuántos muebles caben en la sede, y el
 * estandarte.
 */
export const CLUB_LEVELS = [
    { level: 1, points: 0, unlock: "Sede con 20 muebles" },
    { level: 2, points: 150, unlock: "25 muebles" },
    { level: 3, points: 400, unlock: "30 muebles y estandarte en la puerta" },
    { level: 4, points: 900, unlock: "35 muebles" },
    { level: 5, points: 1800, unlock: "40 muebles y laurel en la insignia" },
];
export function clubLevel(points) {
    let level = 1;
    for (const paso of CLUB_LEVELS)
        if (points >= paso.points)
            level = paso.level;
    return level;
}
/** Cuántos muebles caben en la sede de un club de este nivel. */
export function clubFurnitureCap(level) {
    return 20 + (Math.max(1, Math.min(level, CLUB_LEVELS.length)) - 1) * 5;
}
/* ───────────────────── meta de recaudación y bitácora ───────────────────── */
export const MAX_GOAL_TITLE = 60;
/** Qué clase de cosa pasó. El texto ya viene resuelto; esto es para el icono y el filtro. */
export const CLUB_LOG_KINDS = [
    "fundado",
    "ingreso",
    "salida",
    "expulsion",
    "presidencia",
    "compra",
    "aporte",
    "evento",
    "logo",
    "meta",
    "archivado",
];
/** Cuántas líneas de bitácora se devuelven de una. */
export const CLUB_LOG_PAGE = 30;
/**
 * Avisos del canal social. TODOS van por `client.send` a una cuenta concreta.
 *
 * Ninguno vive en un `Schema` sincronizado: el estado de una sala lo ve todo el que
 * está dentro, y aquí nada es de todos.
 */
export const SocialMsg = {
    /** Un amigo se conectó o se fue. Sólo llega a sus amigos. */
    Presence: "social:presence",
    RequestReceived: "social:request",
    RequestResolved: "social:request:resolved",
    FriendAdded: "social:friend:added",
    FriendRemoved: "social:friend:removed",
    DirectMessage: "social:dm",
    /** Alguien leyó una conversación. Va a las demás pestañas de quien la leyó. */
    ConversationRead: "social:read",
    /**
     * server -> TODAS las conexiones de quien acaba de cambiar algo: "vuelve a mirar".
     *
     * Existe porque una cuenta puede tener varias pestañas y sólo una hizo la petición:
     * las hermanas se quedarían con la lista vieja hasta reconectar. No dice qué cambió
     * —para eso está la foto de la base, que es la verdad— sólo que hay que volver a
     * pedirla, así que es idempotente con la respuesta HTTP que ya aplicó la pestaña
     * que actuó.
     */
    Sync: "social:sync",
    /** Alguien pidió entrar a un club que presides. Sólo al presidente. */
    ClubRequestReceived: "social:club:request",
    /** Tu solicitud de ingreso se resolvió. Sólo a quien la mandó. */
    ClubRequestResolved: "social:club:resolved",
    /** Te sacaron de un club. Sólo al expulsado. */
    ClubExpelled: "social:club:expelled",
    /**
     * Un anuncio nuevo, a los miembros conectados.
     *
     * Es un ACELERADOR: el anuncio ya está en SQLite cuando esto sale, así que perder
     * el aviso —socket caído, pestaña cerrada— no pierde el anuncio. Quien vuelva lo
     * encuentra al abrir el club.
     */
    ClubAnnouncement: "social:club:announcement",
    /**
     * Tu club se reúne AHORA.
     *
     * Va por el canal social y no por la sala donde ocurre porque a quien hay que avisarle
     * es justo al que no está ahí: el canal es lo único que alcanza a una cuenta esté donde
     * esté. Sólo a los miembros, uno por uno — un evento es asunto de su club.
     */
    ClubEventStarted: "social:club:event",
};
/**
 * La clave canónica de un par: el mismo texto lo pidan A→B o B→A.
 *
 * De acá salen la fila única de amistad y el índice de la conversación. Que sea una
 * función compartida es lo que impide que cliente y servidor ordenen distinto.
 */
export function pairKey(a, b) {
    return a < b ? `${a}:${b}` : `${b}:${a}`;
}
//# sourceMappingURL=social.js.map